// RecipientTimeline — modal showing every event for a single recipient,
// sorted ascending by occurred_at. Surfaces hot-signal banners (active
// reading / forwarding) and a Gmail deep-link to draft a follow-up.

import { useEffect, useMemo } from 'react';
import { toMs } from '../hooks/useEventFilter.js';

const TYPE_PILL = {
  email_open:     { label: 'Open',       tone: 'open' },
  email_pixel:    { label: 'Pixel',      tone: 'open' },
  pixel_open:     { label: 'Pixel',      tone: 'open' },
  link_click:     { label: 'Link click', tone: 'click' },
  link_redirect:  { label: 'Redirect',   tone: 'click' },
  pdf_open:       { label: 'PDF open',   tone: 'open' },
  svg_open:       { label: 'SVG open',   tone: 'open' },
  view_open:      { label: 'View open',  tone: 'view' },
  view_visit:     { label: 'View visit', tone: 'view' },
  view_pageshow:  { label: 'Pageshow',   tone: 'view' },
  view_heartbeat: { label: 'Heartbeat',  tone: 'dwell' },
  view_scroll:    { label: 'Scroll',     tone: 'dwell' },
  view_click:     { label: 'View click', tone: 'click' },
  view_copy:      { label: 'Copy',       tone: 'hot' },
  view_blur:      { label: 'Blur',       tone: 'muted' },
  view_focus:     { label: 'Focus',      tone: 'view' },
  view_print:     { label: 'Print',      tone: 'hot' },
};

const TONE_STYLE = {
  open:   { bg: 'color-mix(in srgb, var(--accent) 12%, transparent)',  fg: 'var(--accent)' },
  click:  { bg: 'color-mix(in srgb, var(--accent) 18%, transparent)',  fg: 'var(--accent)' },
  view:   { bg: 'color-mix(in srgb, var(--positive) 14%, transparent)',fg: 'var(--positive)' },
  dwell:  { bg: 'var(--sunken)',                                        fg: 'var(--body)' },
  hot:    { bg: 'color-mix(in srgb, var(--alert) 14%, transparent)',   fg: 'var(--alert)' },
  muted:  { bg: 'var(--sunken)',                                        fg: 'var(--muted)' },
};

export function RecipientTimeline({ recipient, events, onClose }) {
  const ordered = useMemo(() => {
    return [...(events || [])].sort((a, b) => toMs(a.occurred_at) - toMs(b.occurred_at));
  }, [events]);

  const signals = useMemo(() => deriveSignals(ordered), [ordered]);

  // Esc to close, lock body scroll while open.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!recipient) return null;

  const isEmail = /@/.test(recipient);
  const gmailHref = buildGmailDraft(recipient, ordered);
  const firstAt = ordered[0] ? toMs(ordered[0].occurred_at) : null;
  const lastAt  = ordered.length ? toMs(ordered[ordered.length - 1].occurred_at) : null;

  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(5, 28, 44, 0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '4vh 16px', overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Engagement timeline for ${recipient}`}
        tabIndex={-1}
        style={{
          width: '100%', maxWidth: 760, background: 'var(--card)',
          border: '1px solid var(--rule)', borderRadius: 'var(--card-radius)',
          boxShadow: 'var(--card-shadow), 0 12px 40px rgba(0,0,0,0.25)',
        }}
      >
        {/* header */}
        <div style={{
          padding: '14px 18px', borderBottom: '1px solid var(--rule)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div className="eyebrow" style={{ marginBottom: 2 }}>
              {isEmail ? 'Recipient' : 'View ID'}
            </div>
            <div className="display"
                 style={{ color: 'var(--ink)', fontSize: 16, fontWeight: 600,
                          fontFamily: isEmail ? 'var(--body-font)' : 'var(--mono-font)',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {recipient}
            </div>
            <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 4 }}>
              {ordered.length} event{ordered.length === 1 ? '' : 's'}
              {firstAt && lastAt && firstAt !== lastAt && (
                <> · spans {fmtDuration(lastAt - firstAt)}</>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isEmail && (
              <a
                href={gmailHref}
                target="_blank" rel="noreferrer"
                style={{
                  fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                  padding: '6px 10px', textDecoration: 'none',
                  background: 'var(--btn-bg)', color: 'var(--btn-fg)',
                  border: '1px solid var(--btn-bg)',
                }}
              >
                Open in Gmail
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close timeline"
              style={{
                fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                padding: '6px 10px',
                background: 'transparent', color: 'var(--muted)',
                border: '1px solid var(--rule)',
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* signals */}
        {signals.length > 0 && (
          <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--rule)',
                        display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {signals.map((s) => (
              <div key={s.key}
                   style={{
                     padding: '8px 12px', borderRadius: 'var(--radius)',
                     background: s.bg, color: s.fg,
                     border: `1px solid ${s.border}`,
                     fontSize: 11, fontWeight: 600, letterSpacing: '0.02em',
                   }}>
                <span style={{ fontSize: 13, marginRight: 6 }}>{s.icon}</span>
                {s.label}
                {s.detail && (
                  <span style={{ fontWeight: 400, opacity: 0.85, marginLeft: 6 }}>
                    · {s.detail}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* timeline */}
        <div style={{ padding: '14px 18px', maxHeight: '60vh', overflowY: 'auto' }}>
          {ordered.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 12, padding: 12, textAlign: 'center' }}>
              No events for this recipient.
            </div>
          ) : (
            <ol style={{ listStyle: 'none', padding: 0, margin: 0,
                         borderLeft: '1px solid var(--rule)' }}>
              {ordered.map((ev, i) => (
                <TimelineRow key={ev.id || i} event={ev}
                             prev={ordered[i - 1]} index={i} total={ordered.length} />
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}

function TimelineRow({ event, prev, index, total }) {
  const t = toMs(event.occurred_at);
  const dt = prev ? t - toMs(prev.occurred_at) : 0;
  const pill = TYPE_PILL[event.type] || { label: event.type || 'event', tone: 'muted' };
  const tone = TONE_STYLE[pill.tone] || TONE_STYLE.muted;
  const detail = describe(event);

  return (
    <li style={{ position: 'relative', paddingLeft: 18, paddingBottom: index === total - 1 ? 0 : 14 }}>
      <span style={{
        position: 'absolute', left: -5, top: 5,
        width: 9, height: 9, borderRadius: 9999,
        background: tone.fg, border: '2px solid var(--card)',
      }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{
          fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
          padding: '2px 8px', borderRadius: 'var(--radius)',
          background: tone.bg, color: tone.fg, fontWeight: 600,
        }}>
          {pill.label}
        </span>
        <span style={{ fontFamily: 'var(--mono-font)', fontSize: 11, color: 'var(--muted)' }}>
          {fmtAbs(t)}
        </span>
        {prev && (
          <span style={{ fontSize: 10, color: 'var(--dim)' }}>
            +{fmtDuration(dt)}
          </span>
        )}
        {event.classification === 'active_reading' && (
          <span style={{ fontSize: 10, color: 'var(--alert)', fontWeight: 600 }}>· hot</span>
        )}
      </div>
      {detail && (
        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--body)', lineHeight: 1.5 }}>
          {detail}
        </div>
      )}
      {(event.ip || event.country || event.device_type) && (
        <div style={{ marginTop: 2, fontSize: 10, color: 'var(--dim)' }}>
          {event.ip && <span style={{ fontFamily: 'var(--mono-font)' }}>{event.ip}</span>}
          {event.country && <span> · {[event.city, event.region, event.country].filter(Boolean).join(', ')}</span>}
          {event.device_type && <span> · {event.device_type}{event.os ? ` · ${event.os}` : ''}{event.browser ? ` · ${event.browser}` : ''}</span>}
        </div>
      )}
    </li>
  );
}

// ----- signal derivation -------------------------------------------------- //

function deriveSignals(events) {
  if (!events || events.length === 0) return [];
  const out = [];

  let maxDwell = 0;
  let maxScroll = 0;
  let copies = 0;
  let prints = 0;
  const ips = new Set();
  const uas = new Set();
  let opens = 0;

  for (const e of events) {
    if (typeof e.dwell_ms === 'number') maxDwell = Math.max(maxDwell, e.dwell_ms);
    if (typeof e.total_dwell_ms === 'number') maxDwell = Math.max(maxDwell, e.total_dwell_ms);
    if (typeof e.scroll_pct === 'number') maxScroll = Math.max(maxScroll, e.scroll_pct);
    if (typeof e.max_scroll_pct === 'number') maxScroll = Math.max(maxScroll, e.max_scroll_pct);
    if (e.type === 'view_copy') copies++;
    if (e.type === 'view_print') prints++;
    if (['email_open','email_pixel','pixel_open','view_open','view_visit'].includes(e.type)) opens++;
    if (e.ip) ips.add(e.ip);
    if (e.user_agent) uas.add(e.user_agent);
  }

  if (maxDwell >= 30000 && maxScroll >= 75) {
    out.push({
      key: 'reading', icon: '🔥', label: 'ACTIVE READING',
      detail: `${Math.round(maxDwell/1000)}s dwell · ${Math.round(maxScroll)}% scroll`,
      bg: 'color-mix(in srgb, var(--alert) 12%, transparent)',
      fg: 'var(--alert)',
      border: 'color-mix(in srgb, var(--alert) 35%, transparent)',
    });
  }

  if (opens >= 2 && (ips.size >= 2 || uas.size >= 2)) {
    out.push({
      key: 'forwarded', icon: '⚠', label: 'FORWARDED',
      detail: `${opens} opens · ${ips.size} IP${ips.size === 1 ? '' : 's'} · ${uas.size} UA${uas.size === 1 ? '' : 's'}`,
      bg: 'color-mix(in srgb, var(--accent) 12%, transparent)',
      fg: 'var(--accent)',
      border: 'color-mix(in srgb, var(--accent) 35%, transparent)',
    });
  }

  if (copies > 0) {
    out.push({
      key: 'copy', icon: '📋', label: 'CONTENT COPIED',
      detail: `${copies} copy event${copies === 1 ? '' : 's'}`,
      bg: 'color-mix(in srgb, var(--accent) 10%, transparent)',
      fg: 'var(--accent)',
      border: 'color-mix(in srgb, var(--accent) 30%, transparent)',
    });
  }

  if (prints > 0) {
    out.push({
      key: 'print', icon: '🖨', label: 'PRINTED',
      detail: `${prints} print event${prints === 1 ? '' : 's'}`,
      bg: 'var(--sunken)',
      fg: 'var(--ink)',
      border: 'var(--rule)',
    });
  }

  return out;
}

// ----- helpers ------------------------------------------------------------ //

function describe(e) {
  const bits = [];
  if (typeof e.dwell_ms === 'number') bits.push(`dwell ${fmtDuration(e.dwell_ms)}`);
  if (typeof e.total_dwell_ms === 'number') bits.push(`total dwell ${fmtDuration(e.total_dwell_ms)}`);
  if (typeof e.scroll_pct === 'number') bits.push(`scroll ${Math.round(e.scroll_pct)}%`);
  if (typeof e.max_scroll_pct === 'number') bits.push(`max scroll ${Math.round(e.max_scroll_pct)}%`);
  if (e.type === 'view_copy' && e.copied_chars) bits.push(`${e.copied_chars} chars copied`);
  if (e.type === 'link_click' && e.target_url) bits.push(`→ ${e.target_url}`);
  if (e.position) bits.push(`@ ${e.position}`);
  return bits.join(' · ');
}

function fmtDuration(ms) {
  if (!ms || ms < 0) return '0s';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function fmtAbs(t) {
  if (!t) return '—';
  const d = new Date(t);
  return d.toLocaleString(undefined, {
    month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  });
}

function buildGmailDraft(recipient, events) {
  const subject = 'Following up';
  const lastView = [...events].reverse().find((e) =>
    ['view_open','view_visit','view_pageshow','email_open'].includes(e.type)
  );
  const when = lastView ? new Date(toMs(lastView.occurred_at)).toLocaleString() : '';
  const body = [
    'Hi,',
    '',
    when ? `Just circling back on the document I sent — saw it was opened ${when}.` : 'Just circling back on the note I sent.',
    '',
    'Happy to jump on a quick call if useful.',
    '',
    '—',
  ].join('\n');
  const params = new URLSearchParams({
    view: 'cm', fs: '1',
    to: recipient,
    su: subject,
    body,
  });
  return `https://mail.google.com/mail/?${params.toString()}`;
}

export default RecipientTimeline;
